"use client";

import React, { useState, Suspense } from "react";
import Link from "next/link";
import Image from "next/image";
import { Menu } from "lucide-react";
import SearchBox from "./SearchBox";
import MobileMenu from "./MobileMenu";
import { useAuth } from "@/modules/auth/context/AuthContext";
import RoleSwitcher from "@/modules/auth/components/RoleSwitcher";

export default function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user, logout } = useAuth();

  const isPublicUser = user && (user.role === "buyer" || user.role === "fpo_connect");
  const publicUser = isPublicUser ? user : null;

  return (
    <>
      <header className="bg-[#005a2b] text-white w-full z-50 sticky top-0 shadow-md">
        <div className="mx-auto w-full max-w-[1800px] px-4 sm:px-6 lg:px-8">

          {/* DESKTOP HEADER (>= 1024px) */}
          <div className="hidden lg:flex h-20 items-center justify-between gap-8">
            {/* Left: Logo & Branding */}
            <Link href="/" className="flex items-center gap-3 group shrink-0">
              <div className="w-16 h-16 relative bg-white rounded-full shadow-md overflow-hidden border-2 border-white/20 transition-all duration-300 group-hover:scale-105 shrink-0">
                <Image
                  src="/KAB.webp"
                  alt="Karnataka AgriBusiness Logo"
                  fill
                  className="object-contain p-1.5"
                  unoptimized
                />
              </div>
              <span className="text-xl lg:text-3xl  font-semibold font-[--font-mono] text-white   transition-colors ">
                KarnatakaAgribusiness
              </span>
            </Link>

            {/* Center: Search Box */}
            <div className="flex-1 max-w-xl mx-auto px-4">
              <Suspense fallback={<div className="h-10 bg-white/10 rounded-lg animate-pulse" />}>
                <SearchBox />
              </Suspense>
            </div>

            {/* Right: Actions & Auth */}
            <div className="flex items-center gap-6 shrink-0 font-bold text-sm uppercase tracking-wide">
              {publicUser && publicUser.role === "fpo_connect" && (
                <div className="normal-case">
                  <RoleSwitcher />
                </div>
              )}

              <div className="flex items-center gap-4 border-l border-white/10 pl-6">
                {publicUser ? (
                  <>
                    <span className="text-white normal-case font-medium text-xs xl:text-sm">
                      Welcome, <strong className="font-extrabold">{publicUser.name}</strong>
                    </span>
                    <button
                      onClick={logout}
                      className="px-4 py-2 bg-white text-green-800 rounded-lg text-xs font-black hover:bg-green-50 active:scale-95 transition-all cursor-pointer shadow-sm"
                    >
                      Logout
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      href="/auth/register"
                      className="text-white hover:text-green-200 transition-colors font-bold text-xs uppercase"
                    >
                      Register
                    </Link>
                    <Link
                      href="/auth/login"
                      className="px-4 py-2 bg-white text-green-800 rounded-lg text-xs font-black hover:bg-green-50 active:scale-95 transition-all cursor-pointer shadow-sm"
                    >
                      Login
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* MOBILE/TABLET HEADER (< 1024px) */}
          <div className="flex lg:hidden flex-col py-3 gap-3">
            {/* Row 1: Logo & Hamburger */}
            <div className="flex items-center justify-between">
              <Link href="/" className="flex items-center gap-2 group shrink-0">
                <div className="w-10 h-10 relative bg-white rounded-full shadow-sm overflow-hidden border-2 border-white/20 shrink-0">
                  <Image
                    src="/KAB.webp"
                    alt="Karnataka AgriBusiness Logo"
                    fill
                    className="object-contain p-1"
                    unoptimized
                  />
                </div>
                <span className="hidden sm:block text-xl font-black  text-white  drop-shadow-xs">
                  KarnatakaAgribusiness
                </span>
                <span className="block sm:hidden text-xl font-black  text-white drop-shadow-xs">
                  KarnatakaAgribusiness
                </span>
              </Link>

              <button
                onClick={() => setIsMobileMenuOpen(true)}
                className="p-2 text-white hover:bg-white/10 active:bg-white/20 rounded-lg transition-colors cursor-pointer"
                aria-label="Open Menu"
              >
                <Menu size={24} />
              </button>
            </div>

            {/* Row 2: Search Box */}
            <div className="w-full">
              <Suspense fallback={<div className="h-10 bg-white/10 rounded-lg animate-pulse" />}>
                <SearchBox />
              </Suspense>
            </div>
          </div>

        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <MobileMenu
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
      />
    </>
  );
}
